import { hexToRgba } from '../../utils/color.js';
import { resolveImageUrl } from '../../utils/helpers.js';

/**
 * Generates CSS custom properties (design tokens) for the :host element.
 * All other partials reference these variables via var(--ph-*).
 *
 * @param {object} style - The survey style configuration object.
 * @returns {string} CSS :host block with custom properties.
 */
export function generateCSSVariables(style) {
    const textColor = style.textColor || '#45474B';
    const elementColor = style.elementColor || '#AF2D4C';
    const bgColor = style.backgroundColor || '#ffffff';
    const font = style.font || 'Open Sans';
    const btnTextColor = style.buttonTextColor || '#FFFFFF';

    // Standalone background settings
    const bg = style.background || {};
    const info = style.standaloneInfo || {};
    const enableBgImage = bg.enableBackgroundImage === '1' || bg.enableBackgroundImage === true;
    const enableBgOverlay = bg.enableBackgroundOverlay === '1' || bg.enableBackgroundOverlay === true;
    const overlayColor = enableBgImage && enableBgOverlay
        ? hexToRgba(bg.backgroundImageOverlayColor || '#000000', (bg.backgroundTransparency ?? 50) / 100)
        : 'transparent';
    const bgImage = enableBgImage ? `url(${resolveImageUrl(bg.backgroundImage)})` : 'none';
    const bgBlur = bg.backgroundImageBlur ?? 0;
    const bgPosition = bg.backgroundImagePosition || 'background';
    const hasSideImage = enableBgImage && (bgPosition === 'left' || bgPosition === 'right');
    const bgMainBgColor = enableBgImage && bgPosition === 'background'
        ? 'transparent'
        : (bg.backgroundColor || bgColor);

    const contentPos = info.contentPosition || 'left';
    let alignSa = 'flex-start';
    let textAlignSa = 'left';
    let extraBtnJustifySa = 'flex-start';

    if (contentPos === 'center') {
        alignSa = 'center';
        textAlignSa = 'center';
        extraBtnJustifySa = 'center';
    } else if (contentPos === 'right') {
        alignSa = 'flex-end';
        textAlignSa = 'right';
        extraBtnJustifySa = 'flex-end';
    }

    const vars = `
        --ph-font-family: '${font}', sans-serif;
        --ph-element-color: ${elementColor};
        --ph-text-color: ${textColor};
        --ph-text-color-75: ${hexToRgba(textColor, 0.75)};
        --ph-text-color-50: ${hexToRgba(textColor, 0.50)};
        --ph-text-color-32: ${hexToRgba(textColor, 0.32)};
        --ph-text-color-16: ${hexToRgba(textColor, 0.16)};
        --ph-text-color-08: ${hexToRgba(textColor, 0.08)};
        --ph-bg-color: ${bgColor};
        --ph-btn-text-color: ${btnTextColor};
        --ph-box-width: 390px;
        --ph-box-width-medium: 320px;
        --ph-font-large: 16px;
        --ph-font-normal: 14px;
        --ph-font-small: 12px;
        --ph-font-tiny: 10px;
        --ph-sa-bg-image: ${bgImage};
        --ph-sa-bg-blur: ${bgBlur}px;
        --ph-sa-overlay-color: ${overlayColor};
        --ph-sa-bg-main-image: ${enableBgImage && bgPosition === 'background' ? bgImage : 'none'};
        --ph-sa-bg-main-overlay: ${enableBgImage && bgPosition === 'background' ? overlayColor : 'transparent'};
        --ph-sa-bg-main-width: ${hasSideImage ? '50%' : '100%'};
        --ph-sa-bg-main-color: ${bgMainBgColor};
        --ph-sa-show-left: ${enableBgImage && bgPosition === 'left' ? 'block' : 'none'};
        --ph-sa-show-right: ${enableBgImage && bgPosition === 'right' ? 'block' : 'none'};
        --ph-sa-box-outer-width: ${bgPosition === 'background' ? '448px' : '100%'};
        --ph-sa-title-color: ${info.titleColor ?? 'inherit'};
        --ph-sa-title-display: ${info.enableTitle === '1' || info.enableTitle === true ? 'block' : 'none'};
        --ph-sa-subtitle-color: ${info.subtitleColor ?? 'inherit'};
        --ph-sa-subtitle-display: ${info.enableSubtitle === '1' || info.enableSubtitle === true ? 'block' : 'none'};
        --ph-sa-extra-btn-display: ${info.enableExtraButton === '1' || info.enableExtraButton === true ? 'flex' : 'none'};
        --ph-sa-extra-btn-color: ${info.extraButtonBackgroundColor ?? 'transparent'};
        --ph-sa-align-items: ${alignSa};
        --ph-sa-text-align: ${textAlignSa};
        --ph-sa-extra-btn-justify: ${extraBtnJustifySa};`;

    // :host works inside Shadow DOM (onsite, embed, preview).
    // .ph-survey-host works for standalone which has no Shadow DOM.
    return `
    :host {${vars}
    }
    .ph-survey-host {${vars}
    }`;
}
